// packages/core/src/learning/schools/income-expansion.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored exhibit for "Income expansion". Matches boglehead-indexing.ts gold
// standard.
//
// ID-bound fields (the validator + compiler guard these):
//   - figures:        FigureId[]  — held [] for the post-authoring cross-ref
//                     pass. Worldview/exhibit names: ramit-sethi,
//                     grant-sabatier, paula-pant. (Cal Newport and Chris
//                     Guillebeau appear in canon as authors only — NOT
//                     figures-tally entries per the protocol.)
//   - applyActions:   ApplyAction union — type-checked at compile time. Only
//                     live/allowed action types emitted.
//   - featuredLessonIds / debateIds — [] until the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const INCOME_EXPANSION_EXHIBIT: SchoolExhibitEntry = {
	id: 'income-expansion',
	tagline:
		'Income expansion believes earning power can change the whole money game.',
	worldviewSummary:
		'Income expansion is paying attention to the top line of your life. This lens protects you from thinking the only path is cutting smaller and smaller expenses. It accepts that earning more usually requires skill, courage, time, negotiation, risk, or discomfort. The blind spot is pretending income growth is always available to everyone at the same speed. This lens is strongest when it combines ambition with reality: build skills, ask for more, test side income, and protect your energy while you grow.',
	coreBeliefs: [
		'Income expansion believes earning more can create options that cutting alone cannot.',
		'Income expansion believes skills, negotiation, and career moves can matter as much as budgeting.',
		'Income expansion believes a small income jump can change debt payoff, savings, runway, and freedom math.',
		'Income expansion believes people should look for upside, not only damage control.',
		'Income expansion believes income growth still has costs, including stress, time, risk, and attention.',
	],
	warnsAgainst: [
		'Trying to budget your way out of every problem while ignoring earning power.',
		'Confusing loyalty with career strategy when your pay has stopped growing.',
		'Treating side income as free money without counting time, energy, taxes, and stress.',
		'Pretending effort alone fixes every structural barrier.',
	],
	helps: 'This lens helps people who have stabilized the basics and need more upside: a raise, a better role, a sharper skill, a side income test, or a cleaner path to higher earning power. It may not fit when someone needs rest, safety, caregiving room, or stability before adding more pressure.',
	figures: [],
	canon: [
		{
			title: 'I Will Teach You to Be Rich',
			author: 'Ramit Sethi',
			oneLineIdea:
				'Earning more, negotiating, and spending intentionally can matter more than obsessing over tiny cuts.',
		},
		{
			title: 'Financial Freedom',
			author: 'Grant Sabatier',
			oneLineIdea:
				'Income, savings rate, and investing choices interact, and more income can speed up the freedom path.',
		},
		{
			title: 'So Good They Can’t Ignore You',
			author: 'Cal Newport',
			oneLineIdea:
				'Rare and useful skills can create more career control than passion alone.',
		},
		{
			title: 'Side Hustle',
			author: 'Chris Guillebeau',
			oneLineIdea:
				'Small income experiments can teach what people will actually pay for.',
		},
		{
			title: 'Negotiation as a money lever',
			oneLineIdea:
				'A better ask can change years of income if it is backed by timing, proof, and value.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-income-expansion',
	debateIds: [],
	applyActions: [
		{
			label: 'Test how higher income changes your freedom path',
			action: {
				type: 'openSimulator',
				label: 'Simulate higher income',
				scenarioType: 'side-hustle',
			},
		},
		{
			label: 'See whether your current money picture needs more upside',
			action: {
				type: 'openDashboard',
				label: 'Check your current picture',
			},
		},
		{
			label: 'Check how much runway gives you room to make a career move',
			action: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
		},
		{
			label: 'Name the income move worth testing next',
			action: {
				type: 'noToolAction',
				label: 'Pick an income test',
				prompt: 'Write one income move you could test, what proof you need, and what it might cost in time or energy.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'You cannot cut your way into every dream.',
			kind: 'hook',
		},
		{
			line: 'Income expansion asks one uncomfortable question: where is your upside?',
			kind: 'hook',
		},
		{
			line: 'What income move changed your life more than any budget cut?',
			kind: 'interview-question',
		},
		{
			line: 'Where did chasing more money cost you more than you expected?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk lets people test what higher income could change before they gamble their life on a move.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'The app does not just teach cutting back. It teaches how earning power changes options.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Ask someone what skill raised their income, then ask how long it took to prove it.',
			kind: 'creator-prompt',
		},
		{
			line: 'Show the hidden cost of staying underpaid for five more years.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
